"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { cn } from "@/lib/utils";

const TooltipContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);
interface TooltipProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  providerProps?: Omit<TooltipPrimitive.TooltipProviderProps, "children">;
  rootProps?: Omit<TooltipPrimitive.TooltipProps, "children">;
}

const Tooltip = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  providerProps,
  rootProps,
}: TooltipProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  return (
    <TooltipContext.Provider value={{ open, setOpen }}>
      <TooltipPrimitive.Provider {...(providerProps || {})}>
        <TooltipPrimitive.Root
          {...(rootProps || {})}
          open={open}
          onOpenChange={setOpen}
        >
          {children}
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </TooltipContext.Provider>
  );
};

Tooltip.displayName = "Tooltip";

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "tw-z-50 tw-overflow-hidden tw-rounded-4 tw-bg-base-white tw-px-12 tw-py-8 tw-body-1 tw-shadow-md tw-animate-in tw-fade-in-0 tw-zoom-in-95 data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95 data-[side=bottom]:tw-slide-in-from-top-8 data-[side=left]:tw-slide-in-from-right-8 data-[side=right]:tw-slide-in-from-left-8 data-[side=top]:tw-slide-in-from-bottom-8",
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(
  (
    {
      className,
      onClick,
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerCancel,
      ...props
    },
    ref,
  ) => {
    const context = React.useContext(TooltipContext);
    const longPressTimeoutRef = React.useRef<number | null>(null);
    const longPressTriggeredRef = React.useRef(false);

    if (!context) {
      throw new Error("TooltipTrigger must be used within a Tooltip component");
    }

    const { setOpen } = context;
    const isCoarsePointer = () =>
      window.matchMedia("(pointer: coarse)").matches;

    const clearLongPressTimeout = () => {
      if (longPressTimeoutRef.current !== null) {
        window.clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }
    };

    const endLongPress = () => {
      clearLongPressTimeout();
      if (longPressTriggeredRef.current) {
        setOpen(false);
        longPressTriggeredRef.current = false;
      }
    };

    return (
      <TooltipPrimitive.Trigger
        ref={ref}
        className={cn("tw-inline-block", className)}
        {...props}
        onClick={(e) => {
          if (isCoarsePointer() && longPressTriggeredRef.current) {
            e.preventDefault();
            longPressTriggeredRef.current = false;
          }
          onClick?.(e);
        }}
        onPointerDown={(e) => {
          if (e.pointerType === "touch" || isCoarsePointer()) {
            longPressTriggeredRef.current = false;
            clearLongPressTimeout();
            longPressTimeoutRef.current = window.setTimeout(() => {
              longPressTriggeredRef.current = true;
              setOpen(true);
            }, 500);
          }
          onPointerDown?.(e);
        }}
        onPointerUp={(e) => {
          endLongPress();
          onPointerUp?.(e);
        }}
        onPointerLeave={(e) => {
          endLongPress();
          onPointerLeave?.(e);
        }}
        onPointerCancel={(e) => {
          endLongPress();
          onPointerCancel?.(e);
        }}
      />
    );
  },
);

TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipArrow = TooltipPrimitive.Arrow;

export { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger };
